import { useCallback, useEffect, useState } from "react";
import { Download, FileCheck2, FileUp, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { api, formatApiError } from "@/lib/api";

const acceptedConsentTypes =
  "application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function formatSize(bytes) {
  if (!bytes) return "—";
  return `${Math.max(bytes / 1024 / 1024, 0.1).toFixed(1)} MB`;
}

export default function ClientDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ evaluation_id: "", display_name: "" });
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const loadDocuments = useCallback(async () => {
    try {
      const [documentResponse, evaluationResponse] = await Promise.all([
        api.get("/client-documents"),
        api.get("/client-evaluation-requests/mine"),
      ]);
      setDocuments(documentResponse.data);
      setEvaluations(evaluationResponse.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  async function uploadConsent(event) {
    event.preventDefault();
    if (!file) {
      setError("Choose a signed consent PDF or DOCX file first.");
      return;
    }
    try {
      setIsUploading(true);
      setError("");
      const data = new FormData();
      data.append("evaluation_id", form.evaluation_id);
      data.append("display_name", form.display_name);
      data.append("consent_file", file);
      await api.post("/client-documents/consent", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null);
      setForm({ evaluation_id: "", display_name: "" });
      await loadDocuments();
      toast.success("Signed consent received by TAG.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsUploading(false);
    }
  }

  async function downloadDocument(fileRecord) {
    try {
      const response = await api.get(`/client-documents/${fileRecord.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileRecord.original_filename;
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function deleteDocument(documentId) {
    try {
      await api.delete(`/client-documents/${documentId}`);
      setDocuments((current) => current.filter((document) => document.id !== documentId));
      toast.success("Consent document removed.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <div className="app-shell" data-testid="client-documents-shell">
      <AppSidebar activeItem="Documents" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page client-portal-page" data-testid="client-documents-page">
          <section className="page-intro">
            <div>
              <p className="eyebrow">PRIVATE DOCUMENTS</p>
              <h1>Documents</h1>
              <p className="page-subtitle">
                Share signed evaluation consent documents securely with TAG.
              </p>
            </div>
            <div className="client-page-count" data-testid="client-document-count">
              <FileCheck2 size={18} />
              <strong>{documents.length}</strong>
              <span>consent documents</span>
            </div>
          </section>
          {error && (
            <p className="form-message error-message" data-testid="client-documents-error">
              {error}
            </p>
          )}
          <section className="client-document-layout">
            <form
              className="client-consent-form"
              data-testid="client-consent-upload-form"
              onSubmit={uploadConsent}
            >
              <div className="section-heading">
                <div>
                  <h2>Upload signed consent</h2>
                  <p>Only PDF and DOCX evaluation consent documents are accepted.</p>
                </div>
                <ShieldCheck size={20} />
              </div>
              <label>
                Related evaluation request
                <select
                  data-testid="client-consent-evaluation-select"
                  required
                  value={form.evaluation_id}
                  onChange={(event) => setForm({ ...form, evaluation_id: event.target.value })}
                >
                  <option value="">Select evaluation request</option>
                  {evaluations.map((evaluation) => (
                    <option key={evaluation.id} value={evaluation.id}>
                      {evaluation.id.slice(-8)} · {evaluation.status}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Document label
                <input
                  data-testid="client-consent-label-input"
                  maxLength="160"
                  placeholder="e.g., Signed consent — September evaluation"
                  value={form.display_name}
                  onChange={(event) => setForm({ ...form, display_name: event.target.value })}
                />
              </label>
              <label>
                Signed consent file
                <input
                  accept={acceptedConsentTypes}
                  data-testid="client-consent-file-input"
                  required
                  type="file"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
              </label>
              <button
                className="button button-primary"
                data-testid="client-consent-upload-button"
                disabled={isUploading || !evaluations.length}
                type="submit"
              >
                <FileUp size={16} />
                {isUploading ? "Uploading…" : "Upload consent"}
              </button>
            </form>
            <section className="client-document-list" data-testid="client-document-list">
              <div className="section-heading">
                <div>
                  <h2>Shared with TAG</h2>
                  <p>Documents remain available to your client account.</p>
                </div>
              </div>
              {documents.map((document) => (
                <article data-testid={`client-document-${document.id}`} key={document.id}>
                  <FileCheck2 size={20} />
                  <div>
                    <strong data-testid={`client-document-name-${document.id}`}>
                      {document.display_name}
                    </strong>
                    <span>{document.original_filename}</span>
                    <small>
                      {formatSize(document.size)} · Received {new Date(document.uploaded_at).toLocaleDateString()}
                    </small>
                  </div>
                  <div className="client-document-actions">
                    <button
                      aria-label={`Download ${document.display_name}`}
                      className="client-icon-action"
                      data-testid={`download-client-document-${document.id}`}
                      type="button"
                      onClick={() => downloadDocument(document)}
                    >
                      <Download size={16} />
                    </button>
                    <button
                      aria-label={`Remove ${document.display_name}`}
                      className="client-icon-action client-icon-action-danger"
                      data-testid={`delete-client-document-${document.id}`}
                      type="button"
                      onClick={() => deleteDocument(document.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </article>
              ))}
              {!documents.length && (
                <div className="client-empty-state" data-testid="client-documents-empty">
                  <FileCheck2 size={24} />
                  <div>
                    <h2>No consent documents yet</h2>
                    <p>Upload a signed consent after selecting an evaluation request.</p>
                  </div>
                </div>
              )}
            </section>
          </section>
        </main>
      </div>
    </div>
  );
}